/**
 * SkillMatrixToolbar.tsx
 * Filter bar for the Skill Matrix page.
 *
 * Controls:
 *   - Job Class filter (creatable Autocomplete — type-ahead + free entry)
 *   - Work Group filter (creatable Autocomplete — type-ahead + free entry)
 *   - Test Type filter (select from task type list)
 *   - Search box (local filter on displayed employee names, with clear button)
 *   - Undo / Redo icon buttons (shown when canUndo / canRedo are provided)
 *
 * The "Bulk Edit Mode" toggle has been removed. Range selection is now always
 * on — click a cell to focus it, Shift+click to extend, press 1–5 to set level.
 */

import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAddAlt1';
import CheckIcon from '@mui/icons-material/Check';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensityLargeIcon from '@mui/icons-material/DensityLarge';
import type { SelectChangeEvent } from '@mui/material';
import type { MatrixSkill, SkillMatrixLookups } from '../../../services/scheduleSkillMatrix';
import type { Density } from './skill-matrix/density';

export interface SkillMatrixFilters {
  jobClass: string;
  workGroup: string;
  category: string;
  search: string;
}

interface SkillMatrixToolbarProps {
  filters: SkillMatrixFilters;
  skills: MatrixSkill[];
  lookups: SkillMatrixLookups | null;
  onFilterChange: (filters: SkillMatrixFilters) => void;
  /** Current row-density setting. */
  density: Density;
  /** Called when the user picks a new density from the toggle group. */
  onDensityChange: (density: Density) => void;
  /** Undo/redo availability — undefined hides the buttons entirely. */
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  /** Whether the matrix is currently editable. False = view-only (BUG-2026-0098). */
  editMode: boolean;
  /** Toggle between view-only and edit mode. */
  onToggleEditMode: () => void;
  /** RBAC overhaul 2026-08-06: skill levels are admin-only (review rights
   *  derive from them — nobody may raise their own). False hides the Edit
   *  toggle entirely, mirroring the requireMinRole('admin') route gates. */
  canEdit?: boolean;
  /** REQ-07 — open the add-person flow (Admin → Users), if provided. */
  onAddPerson?: () => void;
}

export const SkillMatrixToolbar: React.FC<SkillMatrixToolbarProps> = ({
  filters,
  skills,
  lookups,
  onFilterChange,
  density,
  onDensityChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  editMode,
  onToggleEditMode,
  canEdit = true,
  onAddPerson,
}) => {
  // Job Class: fired by both typing (onInputChange) and selecting (onChange).
  const handleJobClassChange = (_event: React.SyntheticEvent, value: string | null) => {
    onFilterChange({ ...filters, jobClass: value ?? '' });
  };

  const handleJobClassInputChange = (_event: React.SyntheticEvent, value: string) => {
    onFilterChange({ ...filters, jobClass: value });
  };

  const handleWorkGroupChange = (_event: React.SyntheticEvent, value: string | null) => {
    onFilterChange({ ...filters, workGroup: value ?? '' });
  };

  const handleWorkGroupInputChange = (_event: React.SyntheticEvent, value: string) => {
    onFilterChange({ ...filters, workGroup: value });
  };

  const handleCategory = (e: SelectChangeEvent<string>) => {
    onFilterChange({ ...filters, category: e.target.value === 'all' ? '' : e.target.value });
  };

  // Distinct sorted categories derived from the skills prop.
  const categoryOptions = useMemo(
    () =>
      Array.from(new Set(skills.map((t) => t.Category).filter((c): c is string => Boolean(c)))).sort(),
    [skills],
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filters, search: e.target.value });
  };

  const handleSearchClear = () => {
    onFilterChange({ ...filters, search: '' });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        p: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Job Class — creatable Autocomplete */}
      <Autocomplete
        freeSolo
        size="small"
        options={lookups?.jobClasses ?? []}
        value={filters.jobClass || null}
        onChange={handleJobClassChange}
        onInputChange={handleJobClassInputChange}
        sx={{ minWidth: 160 }}
        renderInput={(params) => (
          <TextField {...params} label="Job Class" />
        )}
      />

      {/* Work Group — creatable Autocomplete */}
      <Autocomplete
        freeSolo
        size="small"
        options={lookups?.workGroups ?? []}
        value={filters.workGroup || null}
        onChange={handleWorkGroupChange}
        onInputChange={handleWorkGroupInputChange}
        sx={{ minWidth: 160 }}
        renderInput={(params) => (
          <TextField {...params} label="Work Group" placeholder="All" />
        )}
      />

      {/* Category — filters all test columns to a top-level category */}
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Category</InputLabel>
        <Select
          label="Category"
          value={filters.category || 'all'}
          onChange={handleCategory}
        >
          <MenuItem value="all">All Categories</MenuItem>
          {categoryOptions.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Search — with clear (×) button when there is text */}
      <TextField
        label="Search employees"
        size="small"
        value={filters.search}
        onChange={handleSearch}
        sx={{ minWidth: 180 }}
        InputProps={
          filters.search
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleSearchClear}
                      aria-label="Clear employee search"
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            : undefined
        }
      />

      {/* Density toggle */}
      <Tooltip title="Row density" placement="top">
        <ToggleButtonGroup
          size="small"
          value={density}
          exclusive
          onChange={(_e, v: Density | null) => v && onDensityChange(v)}
          aria-label="Row density"
        >
          <Tooltip title="Compact" placement="top" arrow>
            <ToggleButton value="compact" aria-label="Compact">
              <DensitySmallIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Cozy" placement="top" arrow>
            <ToggleButton value="cozy" aria-label="Cozy">
              <DensityMediumIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Comfortable" placement="top" arrow>
            <ToggleButton value="comfortable" aria-label="Comfortable">
              <DensityLargeIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Tooltip>

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Mode-aware hint: view-only vs editing */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: { xs: 'none', md: 'block' } }}
      >
        {editMode
          ? 'Tip: click a cell to cycle its level, or select a range (click + Shift-click) and press 1–5'
          : 'View only — click Edit to make changes'}
      </Typography>

      {/* Undo / Redo buttons — only rendered when the props are wired up.
          Disabled in view-only mode (nothing to undo when editing is locked). */}
      {(onUndo !== undefined || onRedo !== undefined) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Undo (Ctrl+Z)">
            <span>
              <IconButton
                size="small"
                onClick={onUndo}
                disabled={!editMode || !canUndo}
                aria-label="Undo last skill level change"
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo (Ctrl+Shift+Z)">
            <span>
              <IconButton
                size="small"
                onClick={onRedo}
                disabled={!editMode || !canRedo}
                aria-label="Redo last undone skill level change"
              >
                <RedoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* REQ-07 — Add Person: people are created in Admin → Users (then made
          assignable), which is where this routes. Surfaced here because that's
          where schedulers looked for it. */}
      {onAddPerson && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Add a person in Admin → Users, then set their skill levels here. New users are assignable by default.">
            <Button
              variant="outlined"
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={onAddPerson}
              sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              Add Person
            </Button>
          </Tooltip>
        </>
      )}

      {/* Edit / Done toggle — view-only by default so a stray click never
          changes a skill level (BUG-2026-0098). */}
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      {canEdit && (
      <Tooltip title={editMode ? 'Finish editing — return to view-only' : 'Enable editing'}>
        <Button
          variant={editMode ? 'outlined' : 'contained'}
          color="primary"
          size="small"
          startIcon={editMode ? <CheckIcon /> : <EditIcon />}
          onClick={onToggleEditMode}
          aria-pressed={editMode}
          aria-label={editMode ? 'Finish editing skill matrix' : 'Edit skill matrix'}
          sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
        >
          {editMode ? 'Done' : 'Edit'}
        </Button>
      </Tooltip>
      )}
    </Box>
  );
};
